/**
 * Database Query Optimization Utilities
 * Provides optimized query builders and field selectors
 */

import { supabase } from '../lib/supabase';

/**
 * Optimized field selectors to reduce data transfer
 */
export const OPTIMIZED_SELECTORS = {
  // Minimal match data for lists
  MATCH_LIST: `
    id,
    name,
    start_date,
    status,
    home_score,
    away_score,
    home_team_id,
    away_team_id,
    tournament_id,
    home_team:home_team_id(
      id,
      name,
      logo
    ),
    away_team:away_team_id(
      id,
      name,
      logo
    ),
    tournament:tournament_id(
      id,
      name,
      logo
    )
  `,

  // Essential match details
  MATCH_DETAILS: `
    id,
    name,
    start_date,
    end_date,
    status,
    home_score,
    away_score,
    home_team_id,
    away_team_id,
    tournament_id,
    venue,
    home_team:home_team_id(
      id,
      name,
      logo,
      venue,
      country,
      city
    ),
    away_team:away_team_id(
      id,
      name,
      logo,
      venue,
      country,
      city
    ),
    tournament:tournament_id(
      id,
      name,
      logo,
      country
    )
  `,

  // Minimal scoresheet data
  SCORESHEET_MINIMAL: `
    id,
    match_id,
    team_id,
    stat_type_id,
    value,
    updated_at,
    stat_type:stat_types(
      id,
      stat_key,
      display_name,
      category,
      event_result
    )
  `,

  // Essential organization data
  ORGANIZATION_MINIMAL: `
    id,
    name,
    subdomain,
    logo,
    active
  `,

  // Minimal user data
  USER_MINIMAL: `
    id,
    email,
    first_name,
    last_name,
    role,
    active
  `
};

/**
 * Query builder with pagination support
 */
export class OptimizedQueryBuilder {
  private query: any;
  private pageSize: number = 50;
  private currentPage: number = 0;

  constructor(tableName: string) {
    this.query = supabase.from(tableName);
  }

  /**
   * Add select with optimized fields
   */
  select(fields: string): this {
    this.query = this.query.select(fields);
    return this;
  }

  /**
   * Add where condition
   */
  where(column: string, operator: string, value: any): this {
    this.query = this.query[operator](column, value);
    return this;
  }

  /**
   * Add multiple where conditions
   */
  whereIn(column: string, values: any[]): this {
    this.query = this.query.in(column, values);
    return this;
  }

  /**
   * Add ordering
   */
  orderBy(column: string, ascending: boolean = true): this {
    this.query = this.query.order(column, { ascending });
    return this;
  }

  /**
   * Add pagination
   */
  paginate(page: number = 0, size: number = 50): this {
    this.currentPage = page;
    this.pageSize = size;
    this.query = this.query.range(page * size, (page + 1) * size - 1);
    return this;
  }

  /**
   * Execute query with error handling
   */
  async execute(): Promise<{ data: any[] | null; error: any; count?: number }> {
    try {
      const { data, error, count } = await this.query;
      return { data, error, count };
    } catch (err) {
      console.error('Query execution error:', err);
      return { data: null, error: err };
    }
  }

  /**
   * Execute query and get count
   */
  async executeWithCount(): Promise<{ data: any[] | null; error: any; count: number }> {
    try {
      const { data, error, count } = await this.query;
      return { data, error, count: count || 0 };
    } catch (err) {
      console.error('Query execution error:', err);
      return { data: null, error: err, count: 0 };
    }
  }
}

/**
 * Batch query executor for multiple related queries
 */
export class BatchQueryExecutor {
  private queries: Array<() => Promise<any>> = [];

  /**
   * Add a query to the batch
   */
  addQuery(queryFn: () => Promise<any>): this {
    this.queries.push(queryFn);
    return this;
  }

  /**
   * Execute all queries in parallel
   */
  async executeAll(): Promise<any[]> {
    try {
      const results = await Promise.all(this.queries.map(query => query()));
      return results;
    } catch (error) {
      console.error('Batch query execution error:', error);
      throw error;
    }
  }

  /**
   * Execute queries with error handling
   */
  async executeAllSafe(): Promise<{ results: any[]; errors: any[] }> {
    const results: any[] = [];
    const errors: any[] = [];

    const promises = this.queries.map(async (query, index) => {
      try {
        const result = await query();
        results[index] = result;
      } catch (error) {
        errors[index] = error;
        results[index] = null;
      }
    });

    await Promise.all(promises);
    return { results, errors };
  }
}

/**
 * Smart field selector based on context
 */
export function getOptimizedSelector(context: 'list' | 'details' | 'minimal', entity: string): string {
  const selectors = {
    match: {
      list: OPTIMIZED_SELECTORS.MATCH_LIST,
      details: OPTIMIZED_SELECTORS.MATCH_DETAILS,
      minimal: 'id,name,status,home_score,away_score'
    },
    organization: {
      list: OPTIMIZED_SELECTORS.ORGANIZATION_MINIMAL,
      details: OPTIMIZED_SELECTORS.ORGANIZATION_MINIMAL,
      minimal: 'id,name,logo'
    },
    user: {
      list: OPTIMIZED_SELECTORS.USER_MINIMAL,
      details: OPTIMIZED_SELECTORS.USER_MINIMAL,
      minimal: 'id,email,first_name,last_name'
    },
    scoresheet: {
      list: OPTIMIZED_SELECTORS.SCORESHEET_MINIMAL,
      details: OPTIMIZED_SELECTORS.SCORESHEET_MINIMAL,
      minimal: 'id,stat_type_id,value'
    }
  };

  return selectors[entity as keyof typeof selectors]?.[context] || '*';
}

/**
 * Query performance monitor
 */
export class QueryPerformanceMonitor {
  private static queries: Map<string, { start: number; end?: number; duration?: number }> = new Map();

  static startQuery(queryId: string): void {
    this.queries.set(queryId, { start: Date.now() });
  }

  static endQuery(queryId: string): number {
    const query = this.queries.get(queryId);
    if (query) {
      query.end = Date.now();
      query.duration = query.end - query.start;
      
      // Log slow queries
      if (query.duration > 1000) {
        console.warn(`🐌 Slow query detected: ${queryId} took ${query.duration}ms`);
      }
      
      return query.duration;
    }
    return 0;
  }

  static getStats(): { totalQueries: number; averageDuration: number; slowQueries: number } {
    const queries = Array.from(this.queries.values());
    const completedQueries = queries.filter(q => q.duration !== undefined);
    
    return {
      totalQueries: completedQueries.length,
      averageDuration: completedQueries.reduce((sum, q) => sum + (q.duration || 0), 0) / completedQueries.length,
      slowQueries: completedQueries.filter(q => (q.duration || 0) > 1000).length
    };
  }
}

/**
 * Utility function to create optimized queries
 */
export function createOptimizedQuery(tableName: string): OptimizedQueryBuilder {
  return new OptimizedQueryBuilder(tableName);
}

/**
 * Utility function to create batch executor
 */
export function createBatchExecutor(): BatchQueryExecutor {
  return new BatchQueryExecutor();
}
